<template>
  <div>
    <BubbleHeader :bubble="bubble" />
    <div class="p-6">
      <div v-if="bubble.offline && bubble.offlineMediaResources?.video" class="mb-6 p-4">
        <video
          :src="dataStore + bubble.offlineMediaResources.video"
          class="w-full max-h-[400px] border rounded-lg shadow-lg"
          controls
        >
          <track
            v-if="bubble.offlineMediaResources.transcript"
            kind="subtitles"
            srclang="en"
            :src="dataStore + bubble.offlineMediaResources.transcript"
            label="English"
          />
        </video>
      </div>
      <div v-else-if="bubble.url" class="mb-6 p-4">
        <iframe
          :src="getEmbedUrl(bubble.url)"
          class="w-full h-[400px] border rounded-lg shadow-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          tabindex="-1"
        />
      </div>
      <div class="mb-4">
        <div class="flex justify-between items-start">
          <div>
            <h2 class="text-xl font-bold">{{ bubble.title }}</h2>
            <div v-if="bubble.authorPic" class="flex items-center mt-2">
              <NuxtImg
                v-if="!imageLoadError"
                :src="bubble.authorPic"
                alt="Author"
                class="w-10 h-10 rounded-full mr-3"
                @error="handleImageError"
              />
              <Icon
                v-else
                name="mdi:video"
                class="w-10 h-10 p-2 bg-gray-100 rounded-full mr-3 text-gray-600"
              />
              <div>
                <a
                  v-if="bubble.channelUrl"
                  :href="bubble.channelUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-semibold hover:underline"
                >
                  {{ bubble.author?.[0] }}
                </a>
                <p v-else class="font-semibold">{{ bubble.author?.[0] }}</p>
                <p v-if="bubble.publishDate" class="text-sm text-gray-500">
                  {{ formatDate(bubble.publishDate) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="bubble.videoLength || bubble.publishDate" class="mb-4">
        <p class="text-sm text-gray-700">
          {{ bubble.videoLength ? formatDuration(bubble.videoLength) : '' }}
          {{ bubble.videoLength && bubble.publishDate ? '•' : '' }}
          {{ bubble.publishDate ? formatDate(bubble.publishDate) : '' }}
        </p>
      </div>
      <div v-if="bubble.description" class="mb-6">
        <p class="whitespace-pre-wrap">{{ bubble.description }}</p>
      </div>
      <div v-if="bubble.videoTags && bubble.videoTags.length" class="mb-4">
        <h3 class="text-lg font-semibold mb-2">Tags:</h3>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="tag in bubble.videoTags"
            :key="tag"
            class="bg-gray-200 px-2 py-1 rounded-full text-sm"
          >
            {{ tag }}
          </span>
        </div>
      </div>

      <!-- Stats Section -->
      <div class="bg-card rounded-xl shadow-lg p-6 mt-8">
        <h2 class="text-xl font-semibold mb-4">Your Stats</h2>
        <BubbleStats :bubble-uuid="bubble.uuid" did-label="Watched" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BubbleHeader from '../BubbleHeader.vue'
import { useRuntimeConfig } from '#app'
import type { YouTubeBubble } from '@/stores/interfaces'
import { ref } from 'vue'
import BubbleStats from '../BubbleStats.vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

const props = defineProps<{
  bubble: YouTubeBubble
}>()

// Use the type navigation composable
useTypeNavigation(props.bubble)

const {
  public: { dataStore },
} = useRuntimeConfig()

const getEmbedUrl = (url: string) => {
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.split('v=')[1] || url.split('/').pop()
    return `https://www.youtube.com/embed/${videoId}`
  }
  // Add more video platform embed URL conversions here if needed
  return url
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }
}

const imageLoadError = ref(false)

const handleImageError = () => {
  warn('Failed to load author image:', props.bubble.authorPic)
  imageLoadError.value = true
}
</script>

<style scoped>
/* Scoped styles for VideoDetailedView */
</style>
